import React, { useMemo } from 'react';
import { Paper, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Emoji, SkinTone, SkinToneOption } from '@appTypes/emoji';
import { applySkinTone } from '@utils/skinToneUtils';
import { parseEmoji, isFlagEmoji } from '@utils/twemojiUtils';

interface EmojiCardProps {
  emoji: Emoji;
  onEmojiClick: (emoji: string, event: React.SyntheticEvent<HTMLElement>) => void;
  skinTone?: SkinTone;
  skinToneOptions?: SkinToneOption[];
}

export const EmojiCard = React.memo(
  ({ emoji, onEmojiClick, skinTone = 'default', skinToneOptions }: EmojiCardProps) => {
    const { t } = useTranslation();

    // Get the name using the translation key
    const localizedName = t(emoji.nameKey, emoji.char);

    // Memoize emoji processing
    const { emojiChar, useTwemoji, parsedEmoji } = useMemo(() => {
      const char = applySkinTone(emoji, skinTone, skinToneOptions);
      // Flags never render as native glyphs on Windows, and some code points are too
      // new for the OS font regardless of platform — both fall back to a Twemoji image.
      const shouldRenderAsImage = isFlagEmoji(char) || emoji.renderAsImage === true;
      return {
        emojiChar: char,
        useTwemoji: shouldRenderAsImage,
        parsedEmoji: shouldRenderAsImage ? parseEmoji(char) : '',
      };
    }, [emoji, skinTone, skinToneOptions]);

    return (
      <Tooltip title={localizedName} arrow>
        <Paper
          className="emoji-card"
          elevation={2}
          role="button"
          tabIndex={0}
          aria-label={t('a11y.copyEmoji', { name: localizedName })}
          onClick={(event) => onEmojiClick(emojiChar, event)}
          onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              // Space scrolls the page by default — suppress that.
              if (event.key === ' ') event.preventDefault();
              onEmojiClick(emojiChar, event);
            }
          }}
          sx={{
            padding: 1.5,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'scale(1.1)',
              elevation: 4,
              backgroundColor: '#f5f5f5',
            },
            '&:focus-visible': {
              transform: 'scale(1.1)',
              backgroundColor: '#f5f5f5',
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
            '@media (hover: none)': {
              '&:hover': { transform: 'none' },
              '&:active': { transform: 'scale(0.95)', backgroundColor: '#f0f0f0' },
            },
          }}
        >
          {useTwemoji ? (
            <span className="emoji-char" dangerouslySetInnerHTML={{ __html: parsedEmoji }} />
          ) : (
            <span className="emoji-char">{emojiChar}</span>
          )}
        </Paper>
      </Tooltip>
    );
  }
);
EmojiCard.displayName = 'EmojiCard';
